import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '@configs/i18n';
import { terminateApplicationSession } from '@api/auth/auth';
import { Urls } from '@api/const';
import { createSsoLoginPortalRedirectUrl } from '@api/utils';
import { logException } from '@configs/azureAppInsights/utils';
import { LocalStorageKeys } from '@commons/consts';
import {
  IconProfileOutline,
  IconApps,
  IconSignOut,
  Menu,
  breakpoints,
  useTheme,
  createTheme,
  ThemeProvider,
} from '@fortum/elemental-ui';
import { useResponsiveWidth } from '@utils/useResponsiveWidth';

type AuthWidgetProps = {
  userName?: string;
  forceCondensed?: boolean;
  triggerColor?: string;
  transparent?: boolean;
};

export const AuthWidget: React.FC<AuthWidgetProps> = ({
  userName = '',
  forceCondensed = false,
  triggerColor,
  transparent = false,
}) => {
  const navRef = useRef(null);
  const { t: commonT } = useTranslation<Namespace>('common');
  const { t: glossaryT } = useTranslation<Namespace>('glossary');
  const responsiveWidth = useResponsiveWidth();
  const isSmallScreen = responsiveWidth < breakpoints.l;
  const isMobileScreen = responsiveWidth < breakpoints.s;
  const shouldUseCondensed = forceCondensed || isSmallScreen;

  const clearAppStorage = () => {
    // Clear only app-specific keys, preserve OneTrust cookie consent data
    Object.values(LocalStorageKeys).forEach(key => {
      localStorage.removeItem(key);
    });
  };

  const logout = () => {
    clearAppStorage();
    terminateApplicationSession()
      .then(() => {
        window.location.assign(Urls.ciamSessionTermination);
      })
      .catch(e => {
        logException(
          `Error while terminating application session during logout. ${e}`,
        );
      });
  };

  const changeService = () => {
    window.location.assign(createSsoLoginPortalRedirectUrl());
  };

  const menuItems = [
    ...(!isMobileScreen
      ? [
          {
            value: 'changeService',
            name: commonT('action.changeService'),
            icon: <IconApps aria-hidden="true" />,
            onClick: changeService,
          },
        ]
      : []),
    {
      value: 'logout',
      name: commonT('action.logout'),
      icon: <IconSignOut aria-hidden="true" />,
      onClick: logout,
    },
  ];

  const handleSelect = (value: string) => {
    const selectedItem = menuItems.find(item => item.value === value);
    selectedItem?.onClick?.();
  };

  const baseTheme = useTheme();
  const theme = shouldUseCondensed
    ? createTheme({ variant: 'condensed' }, baseTheme)
    : baseTheme;

  const menuHeader = isMobileScreen ? { title: userName } : undefined;

  return (
    <ThemeProvider theme={theme}>
      <Menu
        aria-label={glossaryT('user.menu')}
        icon={<IconProfileOutline color={triggerColor} />}
        items={menuItems}
        label={userName}
        onSelect={handleSelect}
        showLabel={!isSmallScreen}
        menuWidth="13rem"
        menuHeader={menuHeader}
        variant={shouldUseCondensed ? 'condensed' : 'normal'}
        buttonProps={
          triggerColor || transparent
            ? {
                color: triggerColor,
                variant: shouldUseCondensed ? 'condensed' : 'normal',
              }
            : undefined
        }
        id="auth-widget-menu"
        asDisclosure
        navigationRef={navRef}
      />
    </ThemeProvider>
  );
};
